import { memo, type ReactNode, useCallback } from 'react';

import { type IconComponent, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@air/primitive-select';

import {
  type GuidedVariantEditId,
  isGuidedVariantEditId,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentGuidedVariantEdits';

export type WorkspaceAgentGuidedVariantSettingsOption = {
  icon: IconComponent;
  id: GuidedVariantEditId;
  label: string;
};

export type WorkspaceAgentGuidedVariantSettingsContainerProps = {
  children?: ReactNode;
  onClose: () => void;
  onValueChange: (optionId: GuidedVariantEditId) => void;
  options: readonly WorkspaceAgentGuidedVariantSettingsOption[];
  value: GuidedVariantEditId;
};

export const WorkspaceAgentGuidedVariantSettingsContainer = memo(
  ({ children, onClose, onValueChange, options, value }: WorkspaceAgentGuidedVariantSettingsContainerProps) => {
    const handleValueChange = useCallback(
      (nextValue: string) => {
        if (isGuidedVariantEditId(nextValue)) {
          onValueChange(nextValue);
        }
      },
      [onValueChange],
    );

    return (
      <div className="flex flex-col gap-3 rounded-12 bg-gray-2 p-3" data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_EDITOR">
        <div className="flex items-center justify-between gap-2">
          <Select onValueChange={handleValueChange} value={value}>
            <SelectTrigger
              aria-label="Edit type"
              className="h-auto w-auto border-transparent bg-transparent px-0 font-semibold text-14 shadow-none ring-0 focus:ring-0 data-[state=open]:ring-0"
              data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_EDIT_SELECT"
              size="small"
            />
            <SelectContent>
              {options.map((option) => {
                const Icon = option.icon;

                return (
                  <SelectItem
                    data-edit-id={option.id}
                    data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_EDIT_OPTION"
                    key={option.id}
                    prefix={<Icon aria-hidden className="text-gray-11" size="sm" />}
                    value={option.id}
                  >
                    {option.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <IconButton
            appearance="ghost"
            color="gray"
            data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CLOSE"
            icon={XIcon}
            label="Close"
            onClick={onClose}
            size="small"
          />
        </div>
        {children}
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantSettingsContainer.displayName = 'WorkspaceAgentGuidedVariantSettingsContainer';
